import { useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, Heart, Lock, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Prompt } from '@/types/prompt';
import { useAuthStore } from '@/stores/authStore';
import { useFavoriteStore } from '@/stores/favoriteStore';

interface PromptCardProps {
  prompt: Prompt;
  onViewDetail: (prompt: Prompt) => void;
}

export function PromptCard({ prompt, onViewDetail }: PromptCardProps) {
  const { user } = useAuthStore();
  const { favorites, addFavorite, removeFavorite } = useFavoriteStore();
  const [copied, setCopied] = useState(false);

  const isFavorited = favorites.includes(prompt.id);

  // 检查用户权限
  const hasPermission = () => {
    if (!user) return prompt.permission === 'basic';
    
    const permissionLevel = {
      basic: 1,
      premium: 2,
      admin: 3,
    };

    const userLevel = user.role === 'admin' ? 3 : 
                      user.permissionGroups?.includes('premium') ? 2 : 1;
    const requiredLevel = permissionLevel[prompt.permission];

    return userLevel >= requiredLevel;
  };

  const canAccess = hasPermission();

  // 复制 Prompt 内容
  const handleCopy = async () => {
    if (!canAccess) return;
    
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // 切换收藏状态
  const handleToggleFavorite = async () => {
    if (!user) {
      alert('请先登录');
      return;
    }

    try {
      if (isFavorited) {
        await removeFavorite(prompt.id);
      } else {
        await addFavorite(prompt.id);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  // 权限标识
  const getPermissionBadge = () => {
    const badges = {
      basic: { label: '基础', color: 'bg-secondary text-secondary-foreground border border-border' },
      premium: { label: '高级', color: 'bg-accent text-accent-foreground border border-border' },
      admin: { label: '管理员', color: 'bg-primary text-primary-foreground border border-border' },
    };

    const badge = badges[prompt.permission];

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className={`h-full backdrop-blur-md ${!canAccess ? 'opacity-60' : ''}`}>
        <CardHeader className="p-4 md:p-6">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base md:text-lg flex items-center gap-2">
                {!canAccess && <Lock className="w-4 h-4 text-muted-foreground" />}
                <span className="truncate">{prompt.title}</span>
              </CardTitle>
              <CardDescription className="mt-1.5 line-clamp-2 text-sm">
                {prompt.description}
              </CardDescription>
            </div>
            {getPermissionBadge()}
          </div>
        </CardHeader>

        <CardContent className="p-4 md:p-6 pt-0">
          {/* 标签 */}
          {prompt.tags && prompt.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 md:gap-2 mb-3 md:mb-4">
              {prompt.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 md:py-1 rounded-full text-xs bg-secondary text-secondary-foreground border border-border"
                >
                  {tag}
                </span>
              ))}
              {prompt.tags.length > 3 && (
                <span className="px-2 py-0.5 md:py-1 rounded-full text-xs bg-muted text-muted-foreground border border-border">
                  +{prompt.tags.length - 3}
                </span>
              )}
            </div>
          )}

          {/* 操作按钮 */}
          <div className="flex items-center gap-1.5 md:gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetail(prompt)}
              className="flex-1 text-xs md:text-sm"
              disabled={!canAccess}
            >
              <Eye className="w-3.5 h-3.5 md:w-4 md:h-4 md:mr-2" />
              <span className="hidden sm:inline">查看详情</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              disabled={!canAccess}
              className="px-2 md:px-3"
            >
              {copied ? (
                <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-400" />
              ) : (
                <Copy className="w-3.5 h-3.5 md:w-4 md:h-4" />
              )}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleFavorite}
              disabled={!user}
              className="px-2 md:px-3"
            >
              <Heart
                className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
                  isFavorited ? 'fill-red-500 text-red-500' : ''
                }`}
              />
            </Button>
          </div>

          {/* 作者和时间 */}
          {prompt.author && (
            <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>作者: {prompt.author}</span>
                <span>{new Date(prompt.createdAt * 1000).toLocaleDateString()}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

